import React, { useState, useMemo } from 'react';
import { useTranslation } from '../i18n';
import { getLocalizedItineraries } from '../data/europeItineraries';

export default function ExplorationView({ onSelectItinerary }) {
  const { t, lang } = useTranslation();

  const [filters, setFilters] = useState({
    region: 'all',
    duration: 'all',
    travelStyle: 'all'
  });

  const itineraries = useMemo(() => getLocalizedItineraries(lang), [lang]);

  const filteredItineraries = useMemo(() => {
    const customItinerary = itineraries.find(item => item.id === 'custom-itinerary');
    const otherItineraries = itineraries.filter(item => {
      if (item.id === 'custom-itinerary') return false;

      if (filters.region !== 'all' && item.region !== filters.region) return false;
      if (filters.duration !== 'all' && String(item.durationDays) !== filters.duration) return false;
      if (filters.travelStyle !== 'all' && item.travelStyle !== filters.travelStyle) return false;

      return true;
    }).sort((a, b) => (a.popularity || Number.MAX_SAFE_INTEGER) - (b.popularity || Number.MAX_SAFE_INTEGER));

    return customItinerary ? [customItinerary, ...otherItineraries] : otherItineraries;
  }, [itineraries, filters]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const clearFilters = () => {
    setFilters({ region: 'all', duration: 'all', travelStyle: 'all' });
  };

  const durationLabel = (days) => {
    return t('exploration.durationDays', { days }) || `${days} dias`;
  };

  return (
    <div className="exploration-view">
      <header className="exploration-header">
        <div className="exploration-badge">
          <span className="material-symbols-outlined">explore</span>
          <span>{t('exploration.badge') || 'Destinos em Destaque'}</span>
        </div>
        <h2 className="exploration-title">
          {t('exploration.title') || 'Explore Roteiros pelo Mundo'}
        </h2>
        <p className="exploration-subtitle">
          {t('exploration.subtitle') || 'Selecione um roteiro para iniciar o planejamento imediato com seu assistente de viagem.'}
        </p>
      </header>

      <div className="exploration-filters">
        <div className="filter-chip">
          <span className="material-symbols-outlined filter-chip-icon">public</span>
          <select
            className="filter-select"
            value={filters.region}
            onChange={(e) => handleFilterChange('region', e.target.value)}
            aria-label={t('exploration.filterRegion')}
            title={t('exploration.filterRegion')}
          >
            <option value="all">{t('exploration.filterAll')}</option>
            <option value="europe">{t('exploration.regionEurope')}</option>
            <option value="brazil">{t('exploration.regionBrazil')}</option>
            <option value="americas">{t('exploration.regionAmericas')}</option>
            <option value="asia">{t('exploration.regionAsia')}</option>
            <option value="africa">{t('exploration.regionAfrica')}</option>
          </select>
        </div>

        <div className="filter-chip">
          <span className="material-symbols-outlined filter-chip-icon">schedule</span>
          <select
            className="filter-select"
            value={filters.duration}
            onChange={(e) => handleFilterChange('duration', e.target.value)}
            aria-label={t('exploration.filterDuration')}
            title={t('exploration.filterDuration')}
          >
            <option value="all">{t('exploration.filterAll')}</option>
            {Array.from({ length: 8 }, (_, index) => index + 2).map(days => (
              <option key={days} value={String(days)}>{durationLabel(days)}</option>
            ))}
          </select>
        </div>

        <div className="filter-chip">
          <span className="material-symbols-outlined filter-chip-icon">palette</span>
          <select
            className="filter-select"
            value={filters.travelStyle}
            onChange={(e) => handleFilterChange('travelStyle', e.target.value)}
            aria-label={t('exploration.filterTravelStyle')}
            title={t('exploration.filterTravelStyle')}
          >
            <option value="all">{t('exploration.filterAll')}</option>
            <option value="romantic">{t('exploration.styleRomantic')}</option>
            <option value="historic">{t('exploration.styleHistoric')}</option>
            <option value="beach">{t('exploration.styleBeach')}</option>
            <option value="gastronomic">{t('exploration.styleGastronomic')}</option>
            <option value="urban">{t('exploration.styleUrban')}</option>
            <option value="adventure">{t('exploration.styleAdventure')}</option>
            <option value="cultural">{t('exploration.styleCultural')}</option>
            <option value="nature">{t('exploration.styleNature')}</option>
          </select>
        </div>

        <button 
          className="filter-clear-btn"
          onClick={clearFilters}
        >
          <span className="material-symbols-outlined">refresh</span>
          {t('exploration.filterClear')}
        </button>
      </div>

      <div className="exploration-grid">
        {filteredItineraries.map((item) => (
          <article
            key={item.id}
            className="exploration-card"
            onClick={() => onSelectItinerary(item.prompt)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectItinerary(item.prompt);
              }
            }}
          >
            <div className="exploration-card-media">
              <img
                src={item.image}
                alt={item.destination}
                loading="lazy"
                className="exploration-card-img"
              />
              <span className="exploration-card-tag">{item.tag}</span>
              <span className="exploration-card-duration">
                <span className="material-symbols-outlined">calendar_today</span>
                {item.duration}
              </span>
            </div>

            <div className="exploration-card-body">
              <div className="exploration-card-location">
                <span className="material-symbols-outlined">location_on</span>
                <span>{item.destination}</span>
              </div>
              <h3 className="exploration-card-heading">{item.title}</h3>
              <p className="exploration-card-desc">{item.description}</p>

              <button
                type="button"
                className="exploration-card-action"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectItinerary(item.prompt);
                }}
              >
                <span>{t('exploration.startPlanning') || 'Planejar este Roteiro'}</span>
                <span className="material-symbols-outlined">arrow_forward</span>
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
